'use client';

import Link from 'next/link';
import { ChevronLeft } from 'lucide-react';
import { BRAND_BLUE } from '@voley/shared-types/constants';

export default function ComplianceGuidePage() {
  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link href="/docs" className="text-brand hover:text-[#2a6bb0] font-medium">
                  Docs & Guides
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                  </svg>
                  <span className="ml-4 text-sm font-medium text-gray-600">A2P 10DLC Compliance</span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">
          
          {/* Table of Contents - Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-8">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-gray-400 mb-4">On this page</h3>
              <nav className="space-y-3 border-l-2 border-gray-100 pl-4">
                <a href="#overview" className="block text-sm text-gray-600 hover:text-brand transition-colors">What is A2P 10DLC?</a>
                <a href="#why-matters" className="block text-sm text-gray-600 hover:text-brand transition-colors">Why It Matters</a>
                <a href="#how-voley-handles" className="block text-sm text-gray-600 hover:text-brand transition-colors">How Voley Handles It</a>
                <a href="#registration-process" className="block text-sm text-gray-600 hover:text-brand transition-colors">Registration Process</a>
                <a href="#approval-timeline" className="block text-sm text-gray-600 hover:text-brand transition-colors">Approval Timeline</a>
                <a href="#best-practices" className="block text-sm text-gray-600 hover:text-brand transition-colors">Best Practices</a>
                <a href="#dnc-hipaa" className="block text-sm text-gray-600 hover:text-brand transition-colors">DNC & HIPAA</a>
                <a href="#faq" className="block text-sm text-gray-600 hover:text-brand transition-colors">FAQ</a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">A2P 10DLC Compliance</h1>
              <p className="text-xl text-gray-600">
                Understanding carrier registration requirements for business texting and how Voley keeps your agency compliant.
              </p>
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <span>Estimated time: 10 minutes</span>
              </div>
            </div>

            <div className="bg-brand/10 border border-brand/20 rounded-2xl p-5 mb-8 [&>:first-child]:mt-0">
              <p className="text-sm text-gray-700 mb-0">
                <strong>Related Guides:</strong> This guide covers carrier registration. For federal law requirements, see our <Link href="/docs/tcpa" className="text-brand hover:text-[#2a6bb0]">TCPA Compliance Guide</Link>. For practical tips, see <Link href="/docs/sms-best-practices" className="text-brand hover:text-[#2a6bb0]">SMS Best Practices</Link>.
              </p>
            </div>

            {/* Content Sections */}
            <div className="prose prose-lg max-w-none">

              <section id="overview" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">What is A2P 10DLC?</h2>
                <p className="text-gray-600 mb-4">
                  <strong>A2P 10DLC</strong> stands for Application-to-Person 10-Digit Long Code. It&apos;s the industry standard system that US mobile carriers (AT&T, T-Mobile, Verizon) use to regulate business text messaging.
                </p>
                
                <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 mb-6 [&>:first-child]:mt-0">
                  <h4 className="font-semibold text-gray-900 mb-3">Breaking it down:</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li><strong>A2P (Application-to-Person):</strong> Messages sent from a business application (like Voley) to a person&apos;s phone — as opposed to person-to-person texts between friends.</li>
                    <li><strong>10DLC (10-Digit Long Code):</strong> A standard 10-digit phone number (like 801-555-1234) used for business messaging, rather than short codes (like 12345) or toll-free numbers.</li>
                  </ul>
                </div>

                <p className="text-gray-600 mb-4">
                  Before A2P 10DLC, businesses could send texts from any phone number without registration. This led to widespread spam and fraud. Carriers introduced A2P 10DLC in 2021 to create accountability and protect consumers.
                </p>

                <p className="text-gray-600">
                  Now, any business sending text messages through applications must register their brand and describe their messaging use case. This registration happens through a central registry called The Campaign Registry (TCR).
                </p>
              </section>

              <section id="why-matters" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Why It Matters for Your Agency</h2>
                
                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Without Proper Registration</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Messages get filtered or blocked:</strong> Carriers actively filter unregistered traffic. Your texts may never reach clients.</li>
                  <li><strong>Lower throughput:</strong> Unregistered numbers are severely rate-limited (as low as 1 message per second).</li>
                  <li><strong>Carrier fees and penalties:</strong> Some carriers charge higher fees or impose penalties on unregistered traffic.</li>
                  <li><strong>Reputation damage:</strong> Your number can get flagged as spam, affecting future deliverability.</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">With Proper Registration</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Better deliverability:</strong> Messages are trusted by carriers and reach clients reliably.</li>
                  <li><strong>Higher throughput:</strong> Registered campaigns can send more messages per second.</li>
                  <li><strong>Professional reputation:</strong> Your agency is recognized as a legitimate business sender.</li>
                  <li><strong>Compliance peace of mind:</strong> You&apos;re meeting carrier requirements and reducing legal risk.</li>
                </ul>

                <div className="bg-brand/10 border border-brand/20 rounded-2xl p-5 mb-6 [&>:first-child]:mt-0">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>For Insurance Agencies:</strong> Your clients expect to receive policy updates, renewal reminders, and claim information via text. If your messages aren&apos;t getting through because of carrier filtering, you&apos;re missing critical touchpoints — and potentially creating E&O exposure.
                  </p>
                </div>
              </section>

              <section id="how-voley-handles" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">How Voley Handles Registration</h2>
                <p className="text-gray-600 mb-4">
                  <strong>We handle the entire A2P 10DLC registration process for you.</strong> When you sign up for Voley, we:
                </p>
                
                <ol className="list-decimal pl-6 text-gray-600 mb-6 space-y-3">
                  <li>
                    <strong>Register your brand</strong> with The Campaign Registry using your business information (name, EIN, address, etc.)
                  </li>
                  <li>
                    <strong>Create a messaging campaign</strong> that describes how you&apos;ll use texting (client communication for insurance services)
                  </li>
                  <li>
                    <strong>Associate your phone number</strong> with your registered campaign
                  </li>
                  <li>
                    <strong>Submit for carrier approval</strong> and monitor the status
                  </li>
                  <li>
                    <strong>Notify you when approved</strong> so you can start messaging
                  </li>
                </ol>

                <p className="text-gray-600 mb-4">
                  You don&apos;t need to interact with carriers, fill out TCR forms, or understand the technical details. We&apos;ve built the registration process into our onboarding flow — just provide your business information and we handle the rest.
                </p>

                <div className="bg-brand-green/15 border border-brand-green/40 rounded-2xl p-5 mb-6 [&>:first-child]:mt-0">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>Powered by Twilio:</strong> Voley uses Twilio, a leading communications platform trusted by millions of businesses. Your registration goes through Twilio&apos;s verified submission process, which has established relationships with all major carriers.
                  </p>
                </div>
              </section>

              <section id="registration-process" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">The Registration Process</h2>
                
                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">What You Provide</h3>
                <p className="text-gray-600 mb-4">
                  During Voley onboarding, we collect the information needed for registration:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Business legal name</strong> (as registered with your state)</li>
                  <li><strong>Business address</strong></li>
                  <li><strong>EIN (Employer Identification Number)</strong> or business tax ID</li>
                  <li><strong>Business type</strong> (LLC, Corporation, etc.)</li>
                  <li><strong>Website URL</strong></li>
                  <li><strong>Contact information</strong></li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">What We Submit</h3>
                <p className="text-gray-600 mb-4">
                  Using your information, we register:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white border border-gray-200 rounded-2xl p-5 [&>:first-child]:mt-0">
                    <h4 className="font-semibold text-gray-900 mb-2">Brand Registration</h4>
                    <p className="text-sm text-gray-600 mb-0">
                      Your business identity — confirms you&apos;re a real, legitimate company. Carriers verify your business details against public records.
                    </p>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-2xl p-5 [&>:first-child]:mt-0">
                    <h4 className="font-semibold text-gray-900 mb-2">Campaign Registration</h4>
                    <p className="text-sm text-gray-600 mb-0">
                      How you&apos;ll use messaging — we register you under &quot;Insurance&quot; with use cases like policy updates, claims communication, and appointment reminders.
                    </p>
                  </div>
                </div>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Trust Score</h3>
                <p className="text-gray-600 mb-4">
                  Carriers assign a &quot;trust score&quot; to your brand based on factors like business age, verification status, and industry. Insurance agencies typically receive good trust scores because:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Insurance is a regulated, legitimate industry</li>
                  <li>You have verifiable business credentials</li>
                  <li>Client communication is expected and consented</li>
                </ul>
                <p className="text-gray-600">
                  Higher trust scores mean better throughput and deliverability.
                </p>
              </section>

              <section id="approval-timeline" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Approval Timeline</h2>
                <p className="text-gray-600 mb-4">
                  Registration isn&apos;t instant — carriers need time to verify your business. Here&apos;s what to expect:
                </p>

                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-6">
                  <div className="p-5 border-b border-gray-100">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-brand/20 flex items-center justify-center text-brand font-semibold text-sm mr-3">1</div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Submission</h4>
                        <p className="text-sm text-gray-600">Immediate — we submit your registration when you complete onboarding</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 border-b border-gray-100">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-brand/20 flex items-center justify-center text-brand font-semibold text-sm mr-3">2</div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Brand Verification</h4>
                        <p className="text-sm text-gray-600">1-3 business days — carriers verify your business exists and matches records</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 border-b border-gray-100">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-brand/20 flex items-center justify-center text-brand font-semibold text-sm mr-3">3</div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Campaign Review</h4>
                        <p className="text-sm text-gray-600">1-5 business days — carriers review your messaging use case</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-brand-green/30 flex items-center justify-center text-[#4a9a6a] font-semibold text-sm mr-3">✓</div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Approved & Active</h4>
                        <p className="text-sm text-gray-600">You&apos;ll receive an email notification — messaging is enabled</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-yellow/15 border border-brand-yellow/40 rounded-2xl p-5 mb-6 [&>:first-child]:mt-0">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>Total time:</strong> Most registrations complete within 3-7 business days. Some may take longer if additional verification is needed. We&apos;ll keep you updated on status.
                  </p>
                </div>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">While You Wait</h3>
                <p className="text-gray-600 mb-4">
                  During the approval period, you can still set up your Voley account:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Import contacts from HawkSoft or manually</li>
                  <li>Invite team members</li>
                  <li>Configure settings and preferences</li>
                  <li>Explore the interface</li>
                </ul>
                <p className="text-gray-600">
                  You just won&apos;t be able to send client messages until registration is approved.
                </p>
              </section>

              <section id="best-practices" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Compliance Best Practices</h2>
                <p className="text-gray-600 mb-4">
                  Once you&apos;re registered and messaging, follow these practices to stay compliant:
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">1. Always Have Consent</h3>
                <p className="text-gray-600 mb-4">
                  Only message clients who have agreed to receive texts from your agency. Good consent looks like:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Client provided their phone number directly to your agency</li>
                  <li>Client agreed to receive text messages (verbally, on a form, or in an app)</li>
                  <li>You can document when and how consent was given</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">2. Honor Opt-Outs Immediately</h3>
                <p className="text-gray-600 mb-4">
                  When someone replies STOP (or similar), you must stop messaging them. Voley handles this automatically — we detect opt-out keywords and block further messages to that contact.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">3. Keep Messages Relevant</h3>
                <p className="text-gray-600 mb-4">
                  Your registration describes insurance-related messaging. Stick to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Policy updates and renewals</li>
                  <li>Claims communication</li>
                  <li>Appointment reminders</li>
                  <li>Account service and support</li>
                  <li>Payment reminders</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  Don&apos;t use Voley for unrelated marketing, political messaging, or content outside your registered use case.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">4. Identify Yourself</h3>
                <p className="text-gray-600 mb-4">
                  Include your agency name in messages so clients know who&apos;s contacting them. Example:
                </p>
                <div className="bg-gray-50 rounded-xl p-4 mb-6 font-mono text-sm text-gray-700">
                  &quot;Hi John, this is Sarah from ABC Insurance. Your auto policy renewal is coming up on 2/15. Reply with any questions!&quot;
                </div>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">5. Don&apos;t Send Prohibited Content</h3>
                <p className="text-gray-600 mb-4">
                  Carriers prohibit certain content types regardless of registration. Never send:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Illegal content</li>
                  <li>Phishing or deceptive messages</li>
                  <li>SHAFT content (Sex, Hate, Alcohol, Firearms, Tobacco) unless specifically licensed</li>
                  <li>Messages designed to evade spam filters</li>
                </ul>

                <h3 id="dnc-hipaa" className="text-xl font-semibold mt-6 mb-3 text-gray-900">6. Do Not Call (DNC) Registry</h3>
                <p className="text-gray-600 mb-4">
                  <strong>You are responsible for checking numbers against the National Do Not Call Registry</strong> and any applicable state DNC lists before sending marketing messages. Voley does not automatically scrub numbers against the DNC registry — this is your responsibility.
                </p>
                <p className="text-gray-600 mb-4">
                  Note that transactional messages (policy updates, claims information, appointment confirmations) may be exempt from DNC requirements, but you should consult with legal counsel about what constitutes a transactional vs. marketing message in your context.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">7. Protected Health Information (PHI)</h3>
                <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-6 [&>:first-child]:mt-0">
                  <p className="text-sm text-red-800 mb-0">
                    <strong>Important:</strong> Voley is NOT HIPAA compliant. Do not use Voley to transmit Protected Health Information (PHI) or sensitive medical information. SMS is an inherently insecure channel. While insurance agencies handle various types of information, you must determine what is appropriate to communicate via text and ensure compliance with all applicable privacy laws.
                  </p>
                </div>
              </section>

              <section id="faq" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Frequently Asked Questions</h2>

                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-2xl p-5 [&>:first-child]:mt-0">
                    <h4 className="font-semibold text-gray-900 mb-2">What if my registration is rejected?</h4>
                    <p className="text-sm text-gray-600 mb-0">
                      Rejections are rare for legitimate insurance agencies. If it happens, we&apos;ll work with you to identify the issue (usually a data mismatch) and resubmit. Common fixes include updating your business information to match public records exactly.
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-2xl p-5 [&>:first-child]:mt-0">
                    <h4 className="font-semibold text-gray-900 mb-2">Do I need to register each phone number separately?</h4>
                    <p className="text-sm text-gray-600 mb-0">
                      Your brand is registered once, and your campaign is registered once. Phone numbers are then associated with your campaign. If you add additional numbers later, we associate them with your existing registration — no new approval process needed.
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-2xl p-5 [&>:first-child]:mt-0">
                    <h4 className="font-semibold text-gray-900 mb-2">What happens if I send messages before approval?</h4>
                    <p className="text-sm text-gray-600 mb-0">
                      Voley prevents this — you won&apos;t be able to send client messages until your registration is approved. We&apos;ll show your registration status in the app and notify you when you&apos;re cleared to message.
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-2xl p-5 [&>:first-child]:mt-0">
                    <h4 className="font-semibold text-gray-900 mb-2">Is A2P 10DLC the same as TCPA compliance?</h4>
                    <p className="text-sm text-gray-600 mb-0">
                      No, they&apos;re related but different. A2P 10DLC is carrier registration — it&apos;s about being allowed to send messages technically. TCPA (Telephone Consumer Protection Act) is federal law about consent and opt-outs. You need both: registration with carriers AND proper consent from recipients. See our <Link href="/docs/tcpa" className="text-brand hover:text-[#2a6bb0]">TCPA & SMS Compliance Guide</Link> for detailed information on consent requirements.
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-2xl p-5 [&>:first-child]:mt-0">
                    <h4 className="font-semibold text-gray-900 mb-2">Does this apply to messages I receive?</h4>
                    <p className="text-sm text-gray-600 mb-0">
                      No, A2P 10DLC only applies to outbound messages you send. Clients can text your number at any time — their messages to you are person-to-application (P2A) and don&apos;t have the same restrictions.
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-2xl p-5 [&>:first-child]:mt-0">
                    <h4 className="font-semibold text-gray-900 mb-2">What about toll-free numbers or short codes?</h4>
                    <p className="text-sm text-gray-600 mb-0">
                      Voley uses 10DLC (standard 10-digit numbers) because they&apos;re cost-effective and appropriate for the volume most agencies need. Toll-free and short codes have their own registration processes. If you have specific needs for those number types, contact us.
                    </p>
                  </div>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Need Help?</h2>
                <p className="text-gray-600 mb-4">
                  If you have questions about your registration status, compliance requirements, or messaging best practices, we&apos;re here to help.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    href="/contact"
                    className="inline-flex items-center justify-center px-6 py-3 border-0 text-base font-medium rounded-full text-white hover:shadow-lg transition-all hover:scale-[1.01] no-underline"
                    style={{ backgroundColor: BRAND_BLUE }}
                  >
                    Contact Support
                  </Link>
                  <Link
                    href="/docs/tcpa"
                    className="inline-flex items-center justify-center px-6 py-3 border border-gray-200 text-base font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all no-underline"
                  >
                    TCPA Compliance Guide →
                  </Link>
                </div>
              </section>

            </div>

            {/* Footer Navigation */}
            <div className="mt-16 pt-8 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <Link 
                  href="/docs"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all group"
                >
                  <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                  Back to Docs
                </Link>
                <div className="text-sm text-gray-500">
                  Need help? <Link href="/contact" className="text-brand hover:text-[#2a6bb0] font-medium">Contact our team</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

